/**
 * Per-app inter-app dependencies — the two directions of "which Kin apps
 * may call which". Manager-only; a non-manager who pastes the URL gets the
 * standard read-only notice on top of the server's 403.
 *
 * A dependency is an authorization record, not a Cloudflare service binding:
 * calls route through the control plane (`env.KIN.callApp`). Declaring the
 * edge is the caller manager's move; consenting to it is the target
 * manager's — so a dependency sits "Pending approval" until the other side
 * says yes (auto-approved when one person manages both apps).
 *
 * This page is deliberately code-free: the app's code is written by the AI,
 * not by whoever is reading this, so there is nothing to copy and paste. The
 * wire-level workflow lives in docs/skill/kin-skill.md instead.
 */

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  addDependency,
  api,
  approveInboundDependency,
  listDependencies,
  removeDependency,
  revokeInboundDependency,
} from '../api/client.js';
import { AppCombobox } from '../components/AppCombobox.js';
import { AppTabs } from '../components/AppTabs.js';
import { ManagerOnlyPage } from '../components/ManagerOnly.js';
import { StatusBadge } from '../components/StatusBadge.js';
import { IconLink, IconTrash } from '../components/Icons.js';
import {
  Banner,
  Button,
  Card,
  CardHeader,
  EmptyState,
  Field,
  Mono,
  PageHeader,
  PageSkeleton,
} from '../components/ui.js';
import { relTime } from '../lib/format.js';
import { useAppPageWith } from '../lib/useAppPage.js';
import { runAction, type ActionMessage } from '../lib/runAction.js';
import type { AppSummary, DependenciesResponse } from '../api/types.js';

/** Approval state pill. Not an app status, so StatusBadge doesn't fit. */
function ApprovalBadge({ approvedAt }: { approvedAt: number | null }) {
  const cls =
    approvedAt === null ? 'text-warn bg-warn-soft ring-warn/20' : 'text-ok bg-ok-soft ring-ok/20';
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ring-1 ring-inset ${cls}`}
    >
      <span
        className={`h-1.5 w-1.5 rounded-full ${approvedAt === null ? 'bg-warn' : 'bg-ok'}`}
        aria-hidden
      />
      {approvedAt === null ? 'Pending approval' : 'Approved'}
    </span>
  );
}

/**
 * One row in either direction. Wraps at narrow widths: the identity takes
 * the whole first line on a phone (`basis-full`), so the pill can't land on
 * top of it; from `sm` up everything sits on one line.
 *
 * `name` is only known for apps the viewer can see (it comes from the apps
 * list, which the dependency payload doesn't carry) — an inbound caller
 * managed by someone else falls back to its slug alone.
 */
function DependencyRow({
  slug,
  name,
  status,
  createdAt,
  approvedAt,
  actions,
}: {
  slug: string;
  name?: string | undefined;
  status: string;
  createdAt: number;
  approvedAt: number | null;
  actions: React.ReactNode;
}) {
  return (
    <li className="px-5 py-3.5 text-sm flex flex-wrap items-center gap-x-3 gap-y-2">
      <div className="min-w-0 basis-full sm:basis-auto sm:flex-1">
        <div className="flex items-baseline gap-2 min-w-0">
          <Link
            to={`/apps/${slug}`}
            className="font-medium truncate hover:text-brand transition-colors"
          >
            {name ?? slug}
          </Link>
          {name !== undefined && <Mono className="min-w-0 truncate">{slug}</Mono>}
        </div>
        <p className="text-xs text-muted mt-0.5 truncate">
          {approvedAt !== null ? `Approved ${relTime(approvedAt)}` : `Added ${relTime(createdAt)}`}
        </p>
      </div>
      <span className="inline-flex items-center gap-2 shrink-0">
        {status !== 'active' && <StatusBadge status={status} />}
        <ApprovalBadge approvedAt={approvedAt} />
      </span>
      <span className="inline-flex items-center gap-2 shrink-0 ml-auto sm:ml-0">{actions}</span>
    </li>
  );
}

export function Dependencies() {
  const { slug } = useParams();
  const { app, payload, loading, error, reload } = useAppPageWith<DependenciesResponse>(
    slug,
    listDependencies,
  );
  const [actionMessage, setActionMessage] = useState<ActionMessage | null>(null);
  const [busy, setBusy] = useState(false);
  const [targetSlug, setTargetSlug] = useState('');
  // The apps the viewer can see — both the "App to call" choices and the
  // display names for rows (the dependency payload carries slugs only).
  // `null` while loading; an error falls back to "nothing to call".
  const [apps, setApps] = useState<AppSummary[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get<{ apps: AppSummary[] }>('/api/apps')
      .then((res) => {
        if (!cancelled) setApps(res.apps);
      })
      .catch(() => {
        if (!cancelled) setApps([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) return <PageSkeleton rows={4} />;
  if (error) return <Banner tone="error">{error}</Banner>;
  if (!app || !payload) return null;

  if (!app.is_manager) {
    return (
      <ManagerOnlyPage
        app={app}
        title="Dependencies"
        description="Let this app call another Kin app server-side."
      />
    );
  }

  const { outbound, inbound } = payload;
  const nameBySlug = new Map((apps ?? []).map((a) => [a.slug, a.name]));
  // Can't depend on yourself, and one edge per target — the server rejects
  // both, so keep them out of the menu rather than surfacing a 409.
  const choices = (apps ?? [])
    .filter(
      (a) =>
        a.status === 'active' &&
        a.slug !== app.slug &&
        !outbound.some((d) => d.target_slug === a.slug),
    )
    .sort((a, b) => a.name.localeCompare(b.name));
  // Nothing left to add: either the viewer sees no other app, or every one
  // they can see is already a dependency. Show the form while still loading.
  const canAdd = apps === null || choices.length > 0;

  async function submitAdd(e: React.FormEvent) {
    e.preventDefault();
    if (!slug || !targetSlug) return;
    await runAction({
      do: () => addDependency(slug, { depends_on_slug: targetSlug }),
      onSuccess: (res) => {
        setTargetSlug('');
        return res.approved
          ? `Added ${res.target_slug}. Approved automatically — you manage both apps.`
          : `Added ${res.target_slug}. A manager of ${res.target_slug} must approve before calls flow.`;
      },
      errorPrefix: "Couldn't add dependency",
      setBusy,
      setMessage: setActionMessage,
      reload,
    });
  }

  async function drop(target: string) {
    if (!slug) return;
    if (
      !confirm(
        `Remove the dependency on "${target}"?\n\n` +
          `This app's calls to "${target}" will start failing immediately.`,
      )
    )
      return;
    await runAction({
      do: () => removeDependency(slug, target),
      onSuccess: () => `Removed the dependency on ${target}.`,
      errorPrefix: "Couldn't remove dependency",
      setBusy,
      setMessage: setActionMessage,
      reload,
    });
  }

  async function approve(caller: string) {
    if (!slug) return;
    await runAction({
      do: () => approveInboundDependency(slug, caller),
      onSuccess: (res) =>
        res.alreadyApproved ? `${caller} was already approved.` : `Approved calls from ${caller}.`,
      errorPrefix: "Couldn't approve",
      setBusy,
      setMessage: setActionMessage,
      reload,
    });
  }

  async function revoke(caller: string) {
    if (!slug) return;
    if (
      !confirm(
        `Revoke "${caller}"'s access to this app?\n\n` +
          `Its calls will start failing immediately, and its manager would have to ` +
          `declare the dependency again.`,
      )
    )
      return;
    await runAction({
      do: () => revokeInboundDependency(slug, caller),
      onSuccess: () => `Revoked ${caller}.`,
      errorPrefix: "Couldn't revoke",
      setBusy,
      setMessage: setActionMessage,
      reload,
    });
  }

  return (
    <div className="animate-fade-in">
      <PageHeader
        back={{ to: `/apps/${app.slug}`, label: app.name }}
        title="Dependencies"
        description="Let this app call another Kin app server-side."
      />

      <div className="grid grid-cols-12 gap-6">
        <AppTabs
          slug={app.slug}
          isManager={app.is_manager}
          hasSchedules={app.has_schedules ?? false}
        />
        <main className="col-span-12 lg:col-span-9 xl:col-span-10 min-w-0 space-y-5">
          {actionMessage && (
            <Banner tone={actionMessage.tone} onDismiss={() => setActionMessage(null)}>
              {actionMessage.text}
            </Banner>
          )}

          <Card>
            <p className="px-5 py-4 text-sm text-ink-2">
              Dependencies let this app call another Kin app server-side. The other app&apos;s
              manager must approve before calls go through.
            </p>
          </Card>

          <Card>
            <CardHeader
              title="This app calls"
              description="Apps this app is allowed to call, once approved."
            />
            {!canAdd ? (
              <div className="px-5 py-4">
                <EmptyState
                  icon={<IconLink size={18} />}
                  title="No other apps to call"
                  description="This app can only call Kin apps you have access to, and there are no others left to add."
                />
              </div>
            ) : (
              <form
                onSubmit={(e) => void submitAdd(e)}
                className="px-5 py-4 flex flex-wrap sm:flex-nowrap items-end gap-3 border-b border-line"
              >
                <div className="min-w-0 flex-1">
                  <Field label="App to call" required htmlFor="dep-target">
                    <AppCombobox
                      id="dep-target"
                      apps={choices}
                      value={targetSlug}
                      onChange={setTargetSlug}
                      placeholder={apps === null ? 'Loading apps…' : 'Choose an app…'}
                      required
                      disabled={apps === null}
                      aria-label="App to call"
                    />
                  </Field>
                </div>
                <Button
                  type="submit"
                  variant="primary"
                  size="md"
                  loading={busy}
                  disabled={busy || !targetSlug}
                >
                  Add
                </Button>
              </form>
            )}

            {outbound.length === 0 ? (
              canAdd && (
                <div className="px-5 py-4">
                  <EmptyState
                    icon={<IconLink size={18} />}
                    title="No dependencies yet"
                    description="Pick an app above to let this one call it."
                  />
                </div>
              )
            ) : (
              <ul className="divide-y divide-line">
                {outbound.map((d) => (
                  <DependencyRow
                    key={d.target_slug}
                    slug={d.target_slug}
                    name={nameBySlug.get(d.target_slug)}
                    status={d.target_status}
                    createdAt={d.created_at}
                    approvedAt={d.approved_at}
                    actions={
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => void drop(d.target_slug)}
                        disabled={busy}
                        className="!text-danger hover:!bg-danger-soft"
                      >
                        <IconTrash size={13} />
                        Remove
                      </Button>
                    }
                  />
                ))}
              </ul>
            )}
          </Card>

          <Card>
            <CardHeader
              title="Apps that call this app"
              description="Approve an app before its calls reach this one."
            />
            <p className="px-5 pt-3 text-xs text-muted">
              An approved app can reach every path on this app&apos;s live slot — approve only apps
              you trust.
            </p>
            {inbound.length === 0 ? (
              <div className="px-5 py-4">
                <EmptyState
                  icon={<IconLink size={18} />}
                  title="No inbound requests"
                  description="When another app declares a dependency on this one, it shows up here for your approval."
                />
              </div>
            ) : (
              <ul className="divide-y divide-line mt-3 border-t border-line">
                {inbound.map((d) => (
                  <DependencyRow
                    key={d.caller_slug}
                    slug={d.caller_slug}
                    name={nameBySlug.get(d.caller_slug)}
                    status={d.caller_status}
                    createdAt={d.created_at}
                    approvedAt={d.approved_at}
                    actions={
                      <>
                        {d.approved_at === null && (
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => void approve(d.caller_slug)}
                            disabled={busy}
                          >
                            Approve
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => void revoke(d.caller_slug)}
                          disabled={busy}
                          className="!text-danger hover:!bg-danger-soft"
                        >
                          <IconTrash size={13} />
                          Remove
                        </Button>
                      </>
                    }
                  />
                ))}
              </ul>
            )}
          </Card>
        </main>
      </div>
    </div>
  );
}
